'use client'

import * as React from 'react'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { ArrowRight, Hash, Instagram, Music2, ShieldCheck, Twitter } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'
import { demoPostsFor, getDemoProduct, type DemoPost } from '@/lib/copy/demo-products'

// Client preview for the hashtag-campaign channel tool. A visitor types
// a campaign hashtag and watches a sample aggregated wall update its
// title and tiles, with a per-network mix shown honestly. Routes into
// the real builder (source=hashtag) or signs up. Sample content for
// shape, the real cross-network aggregation + rights flow run in-app.

const NETWORKS: { value: string; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'tiktok', label: 'TikTok', icon: Music2 },
  { value: 'x', label: 'X', icon: Twitter },
  { value: 'threads', label: 'Threads', icon: Hash },
]

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

// Keep the input to a clean hashtag token: letters, numbers, underscore.
function sanitizeTag(raw: string) {
  return raw.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 30)
}

export function HashtagFeedPreview() {
  const reducedMotion = usePrefersReducedMotion()
  const [tag, setTag] = React.useState('summerwithus')

  // Vary the sample pool by tag so the wall feels like it "responds".
  const slug = React.useMemo(() => {
    const t = tag.toLowerCase()
    if (/(glow|skin|beauty|serum)/.test(t)) return 'beauty'
    if (/(run|fit|trail|sport|gym)/.test(t)) return 'sport'
    if (/(home|room|shelf|cosy|cozy)/.test(t)) return 'home'
    return 'fashion'
  }, [tag])
  const posts = React.useMemo(() => demoPostsFor(slug), [slug])
  const tiles = posts.concat(posts).slice(0, 8)
  const display = tag || 'yourcampaign'

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-start">
      {/* Controls */}
      <div className="order-2 lg:order-1 lg:sticky lg:top-24">
        <div className="rounded-2xl border bg-card p-5 sm:p-6">
          <label htmlFor="hashtag-input" className="font-display text-lg font-semibold tracking-tight text-foreground">
            Your campaign hashtag
          </label>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Type a tag, the sample wall retitles and reshuffles. The real wall aggregates live posts across networks.
          </p>
          <div className="mt-4 flex items-center rounded-xl border bg-background pl-3 focus-within:ring-2 focus-within:ring-ring">
            <Hash className="h-4 w-4 text-muted-foreground" aria-hidden />
            <input
              id="hashtag-input"
              type="text"
              inputMode="text"
              value={tag}
              onChange={(e) => setTag(sanitizeTag(e.target.value))}
              placeholder="summerwithus"
              autoComplete="off"
              spellCheck={false}
              aria-describedby="hashtag-help"
              className="w-full bg-transparent px-2 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <p id="hashtag-help" className="mt-2 text-xs text-muted-foreground">
            Letters, numbers and underscores. We strip the rest so it stays a valid tag.
          </p>

          <div className="mt-5">
            <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Aggregated from</span>
            <ul className="mt-2 flex flex-wrap gap-2">
              {NETWORKS.map((n) => {
                const Icon = n.icon
                return (
                  <li key={n.value} className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-foreground">
                    <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
                    {n.label}
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href="/tools/widget-builder?source=hashtag"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              Build it now <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <a
              href={appSignupUrl({ source: 'hashtag', ref: 'tool-hashtag-feed' })}
              className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium text-foreground transition-colors duration-150 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Start a campaign in app
            </a>
          </div>

          <p className="mt-5 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            Every aggregated post runs through a rights request before it can go shoppable, so the wall stays brand-safe and compliant.
          </p>

          <div className="mt-5 border-t pt-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Plan the campaign</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
              <a href="/tools/hashtag-brief" className="inline-flex items-center gap-1 font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                Hashtag brief generator <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </a>
              <a href="/tools/qr-ugc" className="inline-flex items-center gap-1 font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                QR codes for UGC <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Sample wall */}
      <div className="order-1 lg:order-2 rounded-2xl border bg-card p-4 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-base font-semibold tracking-tight text-foreground" aria-live="polite">
            <span className="text-muted-foreground">#</span>{display}
          </h2>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <span className="relative flex h-1.5 w-1.5">
              <span className={cn('absolute inline-flex h-1.5 w-1.5 rounded-full bg-primary/70', !reducedMotion && 'animate-ping')} />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Live sample
          </span>
        </div>

        <div className="rounded-xl border bg-background p-3">
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            {tiles.map((p, i) => (
              <Tile key={`${slug}-${i}`} post={p} reducedMotion={reducedMotion} feature={i === 0} />
            ))}
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          Sample posts shown for shape and styling. Live aggregation across Instagram, TikTok, X and Threads happens in-app once you connect the sources.
        </p>
      </div>
    </div>
  )
}

function Tile({ post, reducedMotion, feature }: { post: DemoPost; reducedMotion: boolean; feature?: boolean }) {
  const hotspot = post.hotspots[0]
  const product = hotspot ? getDemoProduct(hotspot.productId) : null
  return (
    <figure className={cn('relative overflow-hidden rounded-lg', feature ? 'col-span-2 row-span-2 aspect-square' : 'aspect-square')}>
      <ImgWithFallback
        src={post.image}
        alt={post.caption}
        fill
        sizes={feature ? '(min-width: 640px) 24vw, 60vw' : '(min-width: 640px) 12vw, 30vw'}
        className={cn('object-cover', !reducedMotion && 'transition-transform duration-500 ease-out hover:scale-[1.04]')}
      />
      {hotspot && (
        <span
          className="absolute flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground shadow-md ring-2 ring-white/70"
          style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
          aria-hidden
          title={product?.name}
        >
          +
        </span>
      )}
      <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/55 to-transparent p-1.5 text-[10px] font-medium text-white">
        {post.user}
      </figcaption>
    </figure>
  )
}
